import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { CalendarDays, Link2, Loader2, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import { TIDYCAL_URL, LINKTREE_URL, EMAIL } from "@/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-white/60 border border-[#D8D4CA] rounded-xl px-5 py-4 text-base placeholder:text-foreground/40 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-colors duration-200";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", organization: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thanks — your message has been received. I'll be in touch soon.");
      setForm({ name: "", email: "", organization: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or schedule a conversation directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-32 lg:py-40">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-32">
            <motion.p initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6, ease: "easeOut" }} className="text-xs md:text-sm tracking-[0.25em] uppercase font-bold text-accent">
              Bring Phillip to Your Organization
            </motion.p>
            <motion.h2 initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }} className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none mt-6">
              What change are you trying <span className="italic text-primary">to create?</span>
            </motion.h2>
            <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6, delay: 0.15, ease: "easeOut" }} className="mt-8 max-w-md space-y-5 text-base sm:text-lg leading-relaxed font-light text-foreground/80">
              <p>
                Whether you're preparing employees for AI, developing educators,
                planning a leadership retreat, or looking for a technology keynote,
                the conversation starts with your goal.
              </p>
              <p>Tell Phillip about your organization, audience, and desired outcome.</p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }} className="mt-12 space-y-4">
              <a
                href={TIDYCAL_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="tidycal-booking-button"
                className="flex items-center justify-between gap-4 rounded-2xl border border-[#D8D4CA] bg-muted/60 px-6 py-5 hover:border-primary hover:-translate-y-0.5 transition-[border-color,transform] duration-300 group"
              >
                <div className="flex items-center gap-4">
                  <CalendarDays className="w-6 h-6 text-primary" />
                  <div>
                    <div className="font-semibold">Book a Strategy Call</div>
                    <div className="text-sm text-foreground/60">The fastest way to discuss your training or event</div>
                  </div>
                </div>
                <span className="text-primary group-hover:translate-x-1 transition-transform duration-300">→</span>
              </a>
              <a
                href={`mailto:${EMAIL}`}
                data-testid="contact-email-link"
                className="flex items-center gap-4 rounded-2xl border border-[#D8D4CA] px-6 py-5 hover:border-primary transition-colors duration-300"
              >
                <Mail className="w-6 h-6 text-primary" />
                <div>
                  <div className="font-semibold">{EMAIL}</div>
                  <div className="text-sm text-foreground/60">Email</div>
                </div>
              </a>
              <a
                href={LINKTREE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-linktree-link"
                className="flex items-center gap-4 rounded-2xl border border-[#D8D4CA] px-6 py-5 hover:border-primary transition-colors duration-300"
              >
                <Link2 className="w-6 h-6 text-primary" />
                <div>
                  <div className="font-semibold">linktr.ee/banksps</div>
                  <div className="text-sm text-foreground/60">Connect</div>
                </div>
              </a>
            </motion.div>
          </div>
        </div>

        <motion.form
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          onSubmit={submit}
          data-testid="contact-form"
          className="lg:col-span-7 rounded-[2rem] border border-[#D8D4CA] bg-muted/40 p-8 md:p-12 space-y-8 self-start"
        >
          <div>
            <label htmlFor="contact-name" className="block text-sm font-semibold mb-3">Name</label>
            <input
              id="contact-name"
              data-testid="contact-form-name-input"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Jane Doe"
              className={inputCls}
            />
          </div>
          <div>
            <label htmlFor="contact-email" className="block text-sm font-semibold mb-3">Work Email</label>
            <input
              id="contact-email"
              type="email"
              data-testid="contact-form-email-input"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="jane@company.com"
              className={inputCls}
            />
          </div>
          <div>
            <label htmlFor="contact-organization" className="block text-sm font-semibold mb-3">Organization</label>
            <input
              id="contact-organization"
              data-testid="contact-form-organization-input"
              value={form.organization}
              onChange={(e) => setForm({ ...form, organization: e.target.value })}
              placeholder="Company, school district, association, or agency"
              className={inputCls}
            />
          </div>
          <div>
            <label htmlFor="contact-message" className="block text-sm font-semibold mb-3">What are you trying to accomplish?</label>
            <textarea
              id="contact-message"
              data-testid="contact-form-message-input"
              required
              rows={6}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="Tell me about your audience, challenge, event, or professional development goals..."
              className={`${inputCls} resize-none`}
            />
          </div>
          <button
            type="submit"
            disabled={sending}
            data-testid="contact-form-submit-button"
            className="inline-flex items-center gap-3 rounded-full bg-primary text-primary-foreground px-8 py-4 font-semibold hover:-translate-y-1 hover:shadow-lg hover:shadow-primary/20 transition-transform duration-300 disabled:opacity-60 disabled:hover:translate-y-0"
          >
            {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            {sending ? "Sending..." : "Start the Conversation"}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
